import fs from "fs";
import path from "path";
import { loadConfig, loadSecrets, saveGlobalSecrets, getGlobalSecretsLocation, saveGlobalConfig, getGlobalConfigLocation } from "../../config";
import { MetisError } from "../../errors/MetisError";

export async function runConfig(args: string[]) {
  const action = args[0] || "show";

  switch (action) {
    case "show":
      await showConfig();
      break;
    case "set":
      await setConfig(args.slice(1));
      break;
    case "reset":
      await resetConfig();
      break;
    default:
      console.log(`
Usage: metiscode config [action]

Actions:
  show               Show current configuration (default)
  set <key> <value>  Set configuration values
  reset              Reset to default configuration

Set Options:
  provider    AI provider (openai, anthropic, groq)
  model       Model name (gpt-4o, gpt-4-turbo, claude-3-5-sonnet-20241022, etc.)
  temperature Temperature (0.0 to 2.0)
  apikey      API key for the current provider (saved globally)

API Key Priority (highest to lowest):
  1. Environment variables (OPENAI_API_KEY, ANTHROPIC_API_KEY, GROQ_API_KEY)
  2. Global config: ~/.metis/secrets.json
  3. Local project config: ./.metis/secrets.json

Examples:
  metiscode config set provider anthropic
  metiscode config set model gpt-4o
  metiscode config set temperature 0.7
  metiscode config set apikey sk-ant-your-api-key-here
`);
  }
}

async function showConfig() {
  try {
    const config = loadConfig();
    const secrets = loadSecrets();
    const format = process.env.METIS_FORMAT || "pretty";
    
    if (format === "json") {
      // Don't include API keys in JSON output for security
      console.log(JSON.stringify(config, null, 2));
    } else if (format === "yaml") {
      // Would need yaml import for this
      console.log(JSON.stringify(config, null, 2));
    } else {
      console.log("Configuration:");
      console.log(`  Provider: ${config.provider}`);
      console.log(`  Model: ${config.model}`);
      console.log(`  Temperature: ${config.temperature}`);
      
      // Show API key status (but not the actual key for security)
      const providerKey = secrets[config.provider];
      const keyStatus = providerKey ? "configured" : "missing";
      const keyPreview = providerKey ? `${providerKey.substring(0, 8)}...` : "not set";
      console.log(`  API Key: ${keyStatus} (${keyPreview})`);
      
      console.log(`  Safety - Dry Run: ${config.safety?.dryRun ? "enabled" : "disabled"}`);
      console.log(`  Safety - Require Approval: ${config.safety?.requireExecApproval ? "enabled" : "disabled"}`);
      console.log(`  Global config: ${fs.existsSync(getGlobalConfigLocation()) ? "found" : "not found"}`);
      console.log(`  Config location: ${getGlobalConfigLocation()}`);
      console.log(`  Secrets location: ${getGlobalSecretsLocation()}`);
    }
  } catch (error: any) {
    console.error("Error loading config:", error.message);
    process.exitCode = 1;
  }
}

async function resetConfig() {
  const defaultConfig = {
    provider: "",
    model: "",
    temperature: 0.2,
    safety: { dryRun: false, requireExecApproval: true },
    ignore: ["node_modules/**", ".git/**", "dist/**", ".metis/sessions/**"],
  };
  
  saveGlobalConfig(defaultConfig);
  console.log("Global configuration reset to defaults");
  console.log(`Location: ${getGlobalConfigLocation()}`);
}

async function setConfig(args: string[]) {
  if (args.length < 2) {
    console.log("Error: Please specify both key and value");
    console.log("Usage: metiscode config set <key> <value>");
    console.log("\nAvailable keys: provider, model, temperature, apikey");
    return;
  }

  const [key, value] = args;
  
  // Load current config
  const currentConfig = loadConfig();
  
  // Validate and set the key
  switch (key.toLowerCase()) {
    case "provider":
      if (!["openai", "anthropic", "groq"].includes(value.toLowerCase())) {
        console.log("Error: Provider must be one of: openai, anthropic, groq");
        return;
      }
      currentConfig.provider = value.toLowerCase();
      break;
      
    case "model":
      // Validate model based on provider
      const validModels = getValidModels(currentConfig.provider);
      if (validModels.length > 0 && !validModels.includes(value)) {
        console.log(`Warning: Model '${value}' may not be valid for provider '${currentConfig.provider}'`);
        console.log(`Common models for ${currentConfig.provider}:`, validModels.slice(0, 5).join(", "));
        console.log("Setting anyway...");
      }
      currentConfig.model = value;
      break;
      
    case "temperature":
      const temp = parseFloat(value);
      if (isNaN(temp) || temp < 0 || temp > 2) {
        console.log("Error: Temperature must be a number between 0.0 and 2.0");
        return;
      }
      currentConfig.temperature = temp;
      break;
      
    case "apikey":
      // Handle API key separately as it goes to global secrets file
      await setApiKey(currentConfig.provider, value);
      console.log(`✅ API key updated globally for provider: ${currentConfig.provider}`);
      return; // Early return since we don't need to save config file
      
    default:
      console.log(`Error: Unknown configuration key '${key}'`);
      console.log("Available keys: provider, model, temperature, apikey");
      return;
  }
  
  // Write updated config to global location
  try {
    saveGlobalConfig(currentConfig);
    console.log(`✅ Global configuration updated: ${key} = ${value}`);
    console.log(`Location: ${getGlobalConfigLocation()}`);
    
    // Show updated config
    console.log("\nCurrent configuration:");
    console.log(`  Provider: ${currentConfig.provider}`);
    console.log(`  Model: ${currentConfig.model}`);
    console.log(`  Temperature: ${currentConfig.temperature}`);
  } catch (error: any) {
    console.log(`Error saving global configuration: ${error.message}`);
  }
}

async function setApiKey(provider: string, apiKey: string) {
  // Validate API key format
  if (!isValidApiKey(provider, apiKey)) {
    console.log(`Error: Invalid API key format for ${provider}`);
    console.log(getApiKeyFormatHelp(provider));
    return;
  }
  
  try {
    // Save to global secrets location
    saveGlobalSecrets({ [provider]: apiKey });
    console.log(`Global secrets saved to: ${getGlobalSecretsLocation()}`);
    
    // Show priority information
    console.log(`\nAPI Key Priority (highest to lowest):`);
    console.log(`  1. Environment variables (OPENAI_API_KEY, ANTHROPIC_API_KEY, GROQ_API_KEY)`);
    console.log(`  2. Global config: ${getGlobalSecretsLocation()}`);
    console.log(`  3. Local project config: ./.metis/secrets.json`);
    
  } catch (error: any) {
    console.log(`Error saving API key: ${error.message}`);
  }
}

function isValidApiKey(provider: string, apiKey: string): boolean {
  switch (provider.toLowerCase()) {
    case "openai":
      return apiKey.startsWith("sk-") && apiKey.length > 20;
    case "anthropic":
      return apiKey.startsWith("sk-ant-") && apiKey.length > 30;
    case "groq":
      return apiKey.startsWith("gsk_") && apiKey.length > 30;
    default:
      return apiKey.length > 10; // Basic validation for unknown providers
  }
}

function getApiKeyFormatHelp(provider: string): string {
  switch (provider.toLowerCase()) {
    case "openai":
      return "OpenAI API keys should start with 'sk-' and be at least 20 characters long";
    case "anthropic":
      return "Anthropic API keys should start with 'sk-ant-' and be at least 30 characters long";
    case "groq":
      return "Groq API keys should start with 'gsk_' and be at least 30 characters long";
    default:
      return "API key should be at least 10 characters long";
  }
}

function getValidModels(provider: string): string[] {
  switch (provider.toLowerCase()) {
    case "openai":
      return ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-4", "gpt-3.5-turbo"];
    case "anthropic":
      return ["claude-3-5-sonnet-20241022", "claude-3-5-haiku-20241022", "claude-3-opus-20240229"];
    case "groq":
      return ["llama-3.1-70b-versatile", "llama-3.1-8b-instant", "mixtral-8x7b-32768"];
    default:
      return [];
  }
}